import { useCallback, useRef } from 'react';
import { useGenerateDispatch } from "../context/GenerateContext";
import { useAccountContext } from '../context/AccountContext';
import { db } from "../db";
import { getUndeliveredImages } from "../api-helper";
import Swal from 'sweetalert2';

/**
 * Custom hook for managing undelivered images functionality
 * Exports the checkingUndeliveredImages function for use in other components
 */
export const useUndeliveredImages = () => {
    const generateStatusDispatch = useGenerateDispatch();
    const accountContext = useAccountContext();
    const fetchingUndeliveredRef = useRef(false);

    const checkingUndeliveredImages = useCallback(async () => {
        console.log("[UNDELIVERED RECOVERY] Starting undelivered images check");

        // Atomic check and set to prevent race conditions
        if (fetchingUndeliveredRef.current) {
            console.log("[UNDELIVERED RECOVERY] Already processing, skipping");
            return;
        }
        fetchingUndeliveredRef.current = true;

        try {
            // Check if user is logged in
            if (!accountContext.loggedIn || !accountContext.accountDetails?.accountNo) {
                console.log("[UNDELIVERED RECOVERY] User not logged in, skipping");
                return;
            }

            generateStatusDispatch({ type: 'undelivered' });
            console.log("[UNDELIVERED RECOVERY] Fetching undelivered images from API");

            const undeliveredFailedImages = await getUndeliveredImages(accountContext.accountDetails.accountNo);
            console.log(`[UNDELIVERED RECOVERY] API returned ${undeliveredFailedImages.length} undelivered images`);

            if (undeliveredFailedImages.length === 0) {
                console.log("[UNDELIVERED RECOVERY] No undelivered images found");
                generateStatusDispatch({ type: 'done' });
                return;
            }

            // Get all stored images from IndexedDB
            const allStoredImages = await db.images.toArray();
            console.log(`[UNDELIVERED RECOVERY] Found ${allStoredImages.length} stored images in IndexedDB`);

            // Create unique array based on uid
            const uniqueImageMap = new Map();
            allStoredImages.forEach(image => {
                if (image.uid && (!uniqueImageMap.has(image.uid) || image.dataType === "placeholder")) {
                    uniqueImageMap.set(image.uid, image);
                }
            });
            const uniqueArray = Array.from(uniqueImageMap.values());

            console.log(`[UNDELIVERED RECOVERY] Found ${undeliveredFailedImages.length} failed images, ${uniqueArray.length} unique`);

            // Helper functions for matching
            const findFailedMatches = (failedImages, undeliveredImages) => {
                console.log("Finding failed matches");
                return undeliveredImages.filter(largeItem =>
                    failedImages.some(smallItem => smallItem.uid === largeItem.jobUid)
                );
            };

            const removeFailedMatches = (failedImages, undeliveredImages) => {
                console.log("Removing failed matches from undelivered images");
                return undeliveredImages.filter(largeItem =>
                    !failedImages.some(smallItem => smallItem.uid === largeItem.jobUid)
                );
            };

            const undeliveredUids = uniqueArray.filter(image => image.dataType === "placeholder").map(image => image.uid);
            const failedMatches = findFailedMatches(uniqueArray, undeliveredFailedImages);
            console.log("Found undelivered images: ", failedMatches);

            if (failedMatches.length > 0) {
                for (const undeliveredFailedImage of failedMatches) {
                    generateStatusDispatch({ type: 'checkingUndeliveredImages' });
                    console.log(`[UNDELIVERED RECOVERY] Processing undelivered image: ${undeliveredFailedImage.uid}`);

                    try {
                        // Find specific placeholders for this job
                        const placeholders = await db.images
                            .where('uid').equals(undeliveredFailedImage.jobUid)
                            .and(item => !item.image || item.image === '')
                            .and(item => item.dataType === "placeholder")
                            .toArray();

                        console.log(`[UNDELIVERED RECOVERY] Found ${placeholders.length} placeholders for job ${undeliveredFailedImage.jobUid}`);

                        if (placeholders.length > 0) {
                            const placeholder = placeholders[0];
                            console.log(`[UNDELIVERED RECOVERY] Updating placeholder:`, placeholder);

                            // Update the placeholder with the actual image data
                            await db.images.update(placeholder.id, {
                                image: undeliveredFailedImage.image,
                                dataType: "image"
                            });

                            console.log(`[UNDELIVERED RECOVERY] Successfully updated image ${undeliveredFailedImage.uid}`);
                        } else {
                            console.log(`[UNDELIVERED RECOVERY] No placeholders found for job ${undeliveredFailedImage.jobUid}, creating new image entry`);

                            // Create new image entry if no placeholder exists
                            await db.images.add({
                                uid: undeliveredFailedImage.uid,
                                jobUid: undeliveredFailedImage.jobUid,
                                image: undeliveredFailedImage.image,
                                dataType: "image",
                                timestamp: Date.now()
                            });
                        }
                    } catch (error) {
                        console.error(`[UNDELIVERED RECOVERY] Error processing undelivered image ${undeliveredFailedImage.uid}:`, error);
                    }
                }
            }

            // Handle remaining undelivered images
            const remainingImages = removeFailedMatches(uniqueArray, undeliveredFailedImages);
            console.log(`[UNDELIVERED RECOVERY] Processing ${remainingImages.length} remaining images`);

            if (remainingImages.length > 10) {
                await Swal.fire({
                    title: `You have ${remainingImages.length} undelivered images. This is a large amount of image and it might take some time so please be patient.`,
                    icon: "info",
                    showConfirmButton: false,
                    showCancelButton: false,
                    timer: 2000
                });
            }

            // Process remaining images sequentially
            for (const undeliveredImage of remainingImages) {
                generateStatusDispatch({ type: 'checkingUndeliveredImages' });
                console.log(`[UNDELIVERED RECOVERY] Processing remaining image: ${undeliveredImage.uid}`);

                try {
                    // Validate uid and jobUid
                    if (!undeliveredImage.uid || typeof undeliveredImage.uid !== 'string' || undeliveredImage.uid.trim() === '') {
                        console.warn(`[UNDELIVERED RECOVERY] Skipping item with invalid uid:`, undeliveredImage);
                        continue;
                    }
                    if (!undeliveredImage.jobUid || typeof undeliveredImage.jobUid !== 'string' || undeliveredImage.jobUid.trim() === '') {
                        console.warn(`[UNDELIVERED RECOVERY] Skipping item with invalid jobUid:`, undeliveredImage);
                        continue;
                    }

                    // Store the undelivered image
                    await db.images.add({
                        uid: undeliveredImage.uid,
                        jobUid: undeliveredImage.jobUid,
                        image: undeliveredImage.image,
                        dataType: "image",
                        timestamp: Date.now()
                    });

                    console.log(`[UNDELIVERED RECOVERY] Successfully stored remaining image ${undeliveredImage.uid}`);
                } catch (error) {
                    console.error(`[UNDELIVERED RECOVERY] Error storing remaining image ${undeliveredImage.uid}:`, error);
                }
            }

            console.log("[UNDELIVERED RECOVERY] Recovery process completed successfully");

        } catch (error) {
            console.error(`[UNDELIVERED RECOVERY] Top-level error:`, error);
        } finally {
            generateStatusDispatch({ type: 'done' });
            fetchingUndeliveredRef.current = false;
        }
    }, [generateStatusDispatch, accountContext.accountDetails?.accountNo, accountContext.loggedIn]);

    return {
        checkingUndeliveredImages
    };
};